import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Eye,
  Loader2
} from 'lucide-react';

/**
 * SyncProductsButton component with preview-and-approve workflow
 * @param {Object} props
 * @param {Function} props.onPreview - Async function that fetches preview (NO database changes)
 * @param {Function} props.onApply - Async function that applies the sync (modifies database)
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {string} props.variant - Button variant (default: 'outline')
 * @param {string} props.size - Button size (default: 'default')
 */
const SyncProductsButton = ({
  onPreview,
  onApply,
  disabled = false,
  variant = 'outline',
  size = 'default'
}) => {
  // Workflow states: 'idle' | 'fetching-preview' | 'preview-ready' | 'applying' | 'applied'
  const [workflowState, setWorkflowState] = useState('idle');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [appliedResult, setAppliedResult] = useState(null);
  const [error, setError] = useState('');

  const handlePreviewClick = async () => {
    try {
      setWorkflowState('fetching-preview');
      setDialogOpen(true);
      setError('');
      setPreviewData(null);
      setAppliedResult(null);

      // Fetch preview (NO database changes)
      const preview = await onPreview();

      setPreviewData(preview);
      setWorkflowState('preview-ready');
    } catch (err) {
      setError(err.response?.data?.error || err.message || '获取预览失败');
      setWorkflowState('idle');
    }
  };

  const handleApproveAndApply = async () => {
    try {
      setWorkflowState('applying');
      setError('');

      // Apply the sync (modifies database)
      const result = await onApply();

      setAppliedResult(result);
      setWorkflowState('applied');
    } catch (err) {
      setError(err.response?.data?.error || err.message || '应用同步失败');
      setWorkflowState('preview-ready'); // Go back to preview state
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setWorkflowState('idle');
    setPreviewData(null);
    setAppliedResult(null);
    setError('');
  };

  const renderChanges = (changes, total_products, affected_count) => {
    if (affected_count === 0) {
      return (
        <Alert className="border-primary/20 bg-primary/10 mb-4">
          <CheckCircle className="h-4 w-4 text-primary" />
          <AlertDescription>
            所有产品信息已是最新，无需同步
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <div>
        {changes && changes.length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">
              将更新 {affected_count} / {total_products} 个产品：
            </p>
            <div className="max-h-96 overflow-auto space-y-2">
              {changes.map((change, index) => (
                <React.Fragment key={change.product_id || index}>
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-sm">{change.product_id}</span>
                      <Badge variant="outline">
                        {change.fields_changed.length} 个字段
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      {change.fields_changed.map(field => (
                        <div key={field} className="text-xs">
                          <span className="text-muted-foreground">{field}:</span>
                          <div className="flex items-center gap-1 ml-2">
                            <span className="line-through text-destructive">
                              {change[`old_${field}`] || '(空)'}
                            </span>
                            <span>→</span>
                            <span className="font-medium text-success">
                              {change[`new_${field}`] || '(空)'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {index < changes.length - 1 && <Separator />}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDialogContent = () => {
    // Error state
    if (error) {
      return (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      );
    }

    // Fetching preview
    if (workflowState === 'fetching-preview') {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">正在获取预览...</p>
        </div>
      );
    }

    // Preview ready - show changes and await approval
    if (workflowState === 'preview-ready' && previewData) {
      const { changes, total_products, affected_count } = previewData;

      return (
        <div>
          {renderChanges(changes, total_products, affected_count)}

          {affected_count > 0 && (
            <div className="space-y-2 mt-4">
              <Alert className="border-primary/20 bg-primary/10">
                <Eye className="h-4 w-4 text-primary" />
                <AlertDescription className="text-sm">
                  <strong>预览模式：</strong>
                  数据库尚未修改。请检查上述更改，确认无误后点击"批准并应用"。
                  <p className="text-muted-foreground mt-1">
                    同步基于产品的系统内部ID（_id）进行匹配，即使产品编号已更改也能正确同步。
                  </p>
                </AlertDescription>
              </Alert>

              <Alert variant="warning" className="border-warning/20 bg-warning/10">
                <AlertTriangle className="h-4 w-4 text-warning" />
                <AlertDescription className="text-sm">
                  <strong>注意：</strong>
                  同步仅更新产品的描述性信息（名称、规格、分类、产品编号等），
                  <strong>不会修改</strong>订单中的数量和价格。
                  产品匹配基于系统内部ID（_id），而非产品编号。
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      );
    }

    // Applying changes
    if (workflowState === 'applying') {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">正在应用更改...</p>
        </div>
      );
    }

    // Applied - show final results
    if (workflowState === 'applied' && appliedResult) {
      const { changes, total_products, updated_count } = appliedResult;

      return (
        <div>
          <Alert className="border-success/20 bg-success/10 mb-4">
            <CheckCircle className="h-4 w-4 text-success" />
            <AlertDescription>
              成功同步 {updated_count} / {total_products} 个产品
            </AlertDescription>
          </Alert>

          {renderChanges(changes, total_products, updated_count)}

          <Alert variant="warning" className="border-warning/20 bg-warning/10 mt-4">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <AlertDescription className="text-sm">
              <strong>注意：</strong>
              同步仅更新产品的描述性信息（名称、规格、分类、产品编号等），
              <strong>不会修改</strong>订单中的数量和价格。
              产品匹配基于系统内部ID（_id），而非产品编号。
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return null;
  };

  const getDialogTitle = () => {
    if (workflowState === 'fetching-preview') {
      return '获取同步预览';
    }
    if (workflowState === 'preview-ready') {
      return '同步预览（未应用）';
    }
    if (workflowState === 'applying') {
      return '应用更改中';
    }
    if (workflowState === 'applied') {
      return '同步完成';
    }
    return '产品同步';
  };

  const getDialogIcon = () => {
    if (error) {
      return <AlertTriangle className="h-5 w-5 text-destructive" />;
    }
    if (workflowState === 'applied') {
      return <CheckCircle className="h-5 w-5 text-success" />;
    }
    return <Eye className="h-5 w-5 text-primary" />;
  };

  const renderDialogActions = () => {
    // Preview ready - show approve/cancel
    if (workflowState === 'preview-ready' && previewData && previewData.affected_count > 0 && !error) {
      return (
        <>
          <Button variant="ghost" onClick={handleClose} disabled={workflowState === 'applying'}>
            取消
          </Button>
          <Button
            onClick={handleApproveAndApply}
            disabled={workflowState === 'applying'}
          >
            {workflowState === 'applying' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                应用中...
              </>
            ) : (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                批准并应用
              </>
            )}
          </Button>
        </>
      );
    }

    // All other states - just show close button
    return (
      <Button onClick={handleClose}>
        关闭
      </Button>
    );
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handlePreviewClick}
        disabled={disabled || workflowState !== 'idle'}
        className="font-semibold"
      >
        {workflowState === 'fetching-preview' ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            获取预览中...
          </>
        ) : (
          <>
            <RefreshCw className="mr-2 h-4 w-4" />
            同步产品信息
          </>
        )}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={(open) => {
        if (!open && workflowState !== 'applying') handleClose();
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getDialogIcon()}
              {getDialogTitle()}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {renderDialogContent()}
          </div>
          <DialogFooter>
            {renderDialogActions()}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SyncProductsButton;
